// Loosely based on https://github.com/text-mask/text-mask

import isNaN from 'lodash/isNaN';
import memoize from './memoize';
import numberFormatConfig from './number_format_config';

const CARET_TRAP = '[]';
const NUMBER_REGEXP = /\d/;
const MAX_SAFE_INTEGER = 9007199254740991;

// http://stackoverflow.com/a/10899795/604296
const addThousandsSeparator = (integer, thousandsSeparator) =>
  integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);

const getConfig = ({ locale, ...defaults } = {}) => {
  const config = {};

  if (Object.keys(numberFormatConfig).includes(locale)) {
    const { decimal, format = '%s%v', symbol, thousand } = numberFormatConfig[locale];

    const symbolStr = '%s';
    const valueStr = '%v';
    const indexOfValue = format.indexOf(valueStr);
    const prefix = format.substr(0, indexOfValue).replace(symbolStr, symbol);
    const suffix = format.substr(indexOfValue + valueStr.length).replace(symbolStr, symbol);

    Object.assign(config, {
      decimalSymbol: decimal,
      prefix,
      suffix,
      thousandsSeparator: thousand,
    });
  }

  Object.assign(config, defaults);

  return config;
};

const convertToMask = (str) =>
  [...str].map((char) => {
    if (NUMBER_REGEXP.test(char)) {
      return NUMBER_REGEXP;
    }

    return char;
  });

export default memoize((defaults) => {
  const {
    allowDecimal = true,
    allowLeadingZeroes = false,
    convertToNumber = true,
    decimalLimit = 2,
    decimalSymbol = '.',
    includeThousandsSeparator = true,
    integerLimit = null,
    prefix = '',
    suffix = '',
    thousandsSeparator = ',',
  } = getConfig(defaults);

  const prefixLength = (prefix && prefix.length) || 0;
  const suffixLength = (suffix && suffix.length) || 0;

  const getNumberParts = (value, replaceDecimalSymbol = false) => {
    let strValue = typeof value === 'number' ? value.toString() : value;
    let integer;
    let fraction;

    if (strValue.slice(suffixLength * -1) === suffix) {
      strValue = strValue.slice(0, suffixLength * -1);
    }

    if (replaceDecimalSymbol === true) {
      strValue = strValue.replace('.', decimalSymbol);
    }

    const strValueLength = strValue.length;
    const indexOfDecimal = strValue.indexOf(decimalSymbol);
    const hasDecimal = allowDecimal && indexOfDecimal !== -1;
    const integerStart = strValue.slice(0, prefixLength) === prefix ? prefixLength : 0;

    if (hasDecimal) {
      integer = strValue.slice(integerStart, indexOfDecimal);
      fraction = strValue.slice(indexOfDecimal + 1, strValueLength);
      fraction = fraction.replace(/\D+/g, '');

      if (typeof decimalLimit === 'number') {
        fraction = fraction.slice(0, decimalLimit);
      }
    } else {
      integer = strValue.slice(integerStart);
    }

    integer = integer.replace(/\D+/g, '');

    if (!allowLeadingZeroes) {
      integer = integer.replace(/^0+(0$|[^0])/, '$1');
    }

    if (integerLimit && typeof integerLimit === 'number') {
      integer = integer.split(thousandsSeparator).join('').slice(0, integerLimit);
    }

    if (includeThousandsSeparator) {
      integer = addThousandsSeparator(integer, thousandsSeparator);
    }

    return { integer, fraction, hasDecimal };
  };

  function numberMask(value) {
    if (value == null) {
      return [];
    }

    const { integer, fraction, hasDecimal } = getNumberParts(value);

    if (hasDecimal && !integer && !fraction) {
      return [...prefix, 0, decimalSymbol, CARET_TRAP, ...suffix];
    }

    const integerMask = integer && convertToMask(integer);
    const number = hasDecimal
      ? [
          ...integerMask,
          integer && CARET_TRAP, // no need for the caret trap if there's no integer value
          decimalSymbol,
          CARET_TRAP,
          ...convertToMask(fraction),
        ].filter(Boolean)
      : integerMask;

    return [...prefix, CARET_TRAP, ...number, CARET_TRAP, ...suffix];
  }

  numberMask.parseValue = (value) => {
    if (value == null) {
      return null;
    }

    if (convertToNumber === false) {
      return typeof value === 'number' ? value.toString() : value;
    }

    // remove prefix
    const strValue = typeof value === 'number' ? value.toString() : value;
    const numberStart = strValue.slice(0, prefixLength) === prefix ? prefixLength : 0;
    let numberStr = strValue.slice(numberStart);

    // remove suffix
    if (numberStr.slice(suffixLength * -1) === suffix) {
      numberStr = numberStr.slice(0, suffixLength * -1);
    }

    numberStr = numberStr.split(thousandsSeparator).join('').replace(decimalSymbol, '.');

    let number =
      allowDecimal && numberStr.indexOf('.') !== -1
        ? parseFloat(numberStr, 10)
        : parseInt(numberStr, 10);

    // we can no longer rely on Number in javascript, fall back to string
    if (number > MAX_SAFE_INTEGER) {
      number = numberStr.slice(-1) === '.' ? numberStr.slice(0, -1) : numberStr;
    }

    return isNaN(number) ? null : number;
  };

  numberMask.unparseValue = (value) => {
    if (value == null) {
      return '';
    }

    if (convertToNumber === false) {
      return typeof value === 'number' ? value.toString() : value;
    }

    const { integer, fraction } = getNumberParts(value, true);

    return allowDecimal && fraction ? `${integer}${decimalSymbol}${fraction}` : integer;
  };

  return numberMask;
});
